import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ProfessionalCandlestickChart } from "../components/trading/ProfessionalCandlestickChart";
import { DepthOfMarket } from "../components/trading/DepthOfMarket";
import { Watchlist } from "../components/trading/Watchlist";
import { OrderPanel } from "../components/trading/OrderPanel";
import { useWebSocket } from "../hooks/useWebSocket";
import { useAuth } from "../hooks/useAuth";
import { usePortfolio } from "../hooks/usePortfolio";
import type {
  MarketPair,
  OrderPayload,
  Order,
  Timeframe,
  Ticker,
  Project,
  Account,
  TradingPosition,
  AccountTransaction,
} from "../types/api";
import * as marketService from "../services/marketService";
import * as projectService from "../services/projectService";
import * as orderService from "../services/orderService";
import * as accountService from "../services/accountService";
import * as tradingService from "../services/tradingService";
import { useToast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useTranslation } from "react-i18next";
import { Pagination } from "../components/table/DataTable";

// Taux de conversion simple EUR → USD pour l'affichage du solde global.
// TODO: remplacer par un service de FX temps réel si nécessaire.
const EUR_TO_USD_RATE = 1.05;

/**
 * Convertit un projet en MarketPair et enrichit avec les données de marché
 * Retourne null si le symbole n'est pas disponible sur Binance
 */
const projectToMarketPair = async (
  project: Project,
): Promise<MarketPair | null> => {
  const symbol = `${project.symbol}/USD`;

  try {
    // Vérifier d'abord si le symbole existe sur Binance
    const exists = await marketService.checkSymbolExists(symbol);
    if (!exists) {
      console.warn(
        `Symbole ${symbol} non disponible sur Binance, projet ignoré`,
      );
      return null;
    }

    // Récupérer les données de marché pour ce symbole
    const ticker = await marketService.getTicker(symbol);
    return {
      symbol,
      baseAsset: project.symbol,
      quoteAsset: "USD",
      price: ticker.price,
      change24h: ticker.change24h,
      changePercent24h: ticker.changePercent24h,
      volume24h: ticker.volume24h,
      high24h: ticker.high24h,
      low24h: ticker.low24h,
      projectId: project.id,
      projectName: project.name,
    };
  } catch (error) {
    // Si les données de marché ne sont pas disponibles, ignorer le projet
    console.warn(
      `Données de marché non disponibles pour ${symbol}, projet ignoré:`,
      error,
    );
    return null;
  }
};

/**
 * Page de trading avec graphiques en temps réel, DOM, watchlist et panneau d'ordre
 */
const TradingPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);
  const { balances, balancesLoading, fetchBalances } = usePortfolio();
  const [selectedPair, setSelectedPair] = useState<MarketPair | null>(null);
  const [availablePairs, setAvailablePairs] = useState<MarketPair[]>([]);
  const [timeframe, setTimeframe] = useState<Timeframe>("1h");
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [openOrders, setOpenOrders] = useState<Order[]>([]);
  const [orderHistory, setOrderHistory] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const hasLoadedOrdersRef = useRef(false);
  const hasLoadedHistoryRef = useRef(false);
  const [tradingAccounts, setTradingAccounts] = useState<Account[]>([]);
  const [positions, setPositions] = useState<TradingPosition[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [pnlTransactions, setPnlTransactions] = useState<AccountTransaction[]>(
    [],
  );
  const [loadingPnlHistory, setLoadingPnlHistory] = useState(false);
  const [pnlCurrency, setPnlCurrency] = useState<string>("USD");
  const [positionsPage, setPositionsPage] = useState(1);
  const [pnlHistoryPage, setPnlHistoryPage] = useState(1);
  const [recentClosedSymbol, setRecentClosedSymbol] = useState<string | null>(
    null,
  );
  const closeIndicatorTimeout = useRef<NodeJS.Timeout | null>(null);
  const cancelledOrdersNotifiedRef = useRef<Set<number>>(new Set());
  const executedOrdersNotifiedRef = useRef<Set<number>>(new Set());
  const previousOpenOrdersRef = useRef<Order[]>([]);
  /** Signatures pour ne pas setState (→ re-render) si ordres/historique inchangés (rafraîchissement en arrière-plan). */
  const lastOpenOrdersSigRef = useRef<string>("");
  const lastOrderHistorySigRef = useRef<string>("");
  const { t, i18n } = useTranslation();
  const positionsPerPage = 5;
  const pnlHistoryPerPage = 10;
  const totalPositionsPages = Math.max(
    1,
    Math.ceil(positions.length / positionsPerPage),
  );
  const paginatedPositions = useMemo(() => {
    if (positions.length === 0) {
      return [];
    }
    const start = (positionsPage - 1) * positionsPerPage;
    return positions.slice(start, start + positionsPerPage);
  }, [positions, positionsPage, positionsPerPage]);

  const totalPnlPages = Math.max(
    1,
    Math.ceil(pnlTransactions.length / pnlHistoryPerPage),
  );
  const paginatedPnlTransactions = useMemo(() => {
    if (pnlTransactions.length === 0) {
      return [];
    }
    const start = (pnlHistoryPage - 1) * pnlHistoryPerPage;
    return pnlTransactions.slice(start, start + pnlHistoryPerPage);
  }, [pnlTransactions, pnlHistoryPage, pnlHistoryPerPage]);

  useEffect(() => {
    if (positionsPage > totalPositionsPages) {
      setPositionsPage(totalPositionsPages);
    }
  }, [positionsPage, totalPositionsPages]);

  /** Ne dépend que de totalPnlPages pour éviter un effet à chaque changement de page. */
  useEffect(() => {
    setPnlHistoryPage((prev) => {
      if (totalPnlPages >= 1 && prev > totalPnlPages) {
        return totalPnlPages;
      }
      return prev;
    });
  }, [totalPnlPages]);

  const loadPnlHistory = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!user?.id) {
        setPnlTransactions([]);
        if (!silent) setLoadingPnlHistory(false);
        return;
      }
      try {
        if (!silent) setLoadingPnlHistory(true);
        const accounts = await accountService.getUserAccounts();
        const tradingAccounts = accounts.filter((a) => a.type === "trading");
        if (!tradingAccounts.length) {
          setPnlTransactions([]);
          return;
        }
        // Utiliser la devise du premier compte trading (en pratique, tous sont en USD aujourd'hui)
        setPnlCurrency(tradingAccounts[0].currency || "USD");

        const allTransactions: AccountTransaction[] = [];
        for (const acc of tradingAccounts) {
          const txs = await accountService.getAccountTransactions(
            acc.id,
            100,
            0,
          );
          allTransactions.push(...txs);
        }

        const settlements = allTransactions
          .filter((tx) => tx.type === "trade_settlement")
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .slice(0, 20);
        setPnlTransactions(settlements);
      } catch (error) {
        // Ne pas bloquer la page de trading si l'historique P&L échoue
        console.error("Erreur chargement historique P&L:", error);
        setPnlTransactions([]);
      } finally {
        if (!silent) setLoadingPnlHistory(false);
      }
    },
    // Même logique que loadPositions / loadTradingAccounts : id stable pour ne pas
    // recréer le callback à chaque rendu du contexte auth → évite de relancer
    // l’effet P&L (chargement non silencieux + pagination qui « clignote »).
    [user?.id],
  );

  const loadTradingAccounts = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!user) {
        setTradingAccounts([]);
        if (!silent) setLoadingAccounts(false);
        return;
      }
      try {
        if (!silent) setLoadingAccounts(true);
        const accounts = await accountService.getUserAccounts();
        setTradingAccounts(
          accounts.filter((account) => account.type === "trading"),
        );
      } catch (error: any) {
        // Ne pas logger les erreurs 401 (session expirée)
        if (
          error?.response?.status !== 401 &&
          !error?.includes?.("Session expirée")
        ) {
          console.error("Erreur lors du chargement des comptes:", error);
        }
        setTradingAccounts([]);
      } finally {
        if (!silent) setLoadingAccounts(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [user?.id],
  );

  const loadPositions = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!user) {
        setPositions([]);
        if (!silent) setLoadingPositions(false);
        return;
      }
      try {
        if (!silent) setLoadingPositions(true);
        const data = await tradingService.getPositions();
        setPositions(data);
      } catch (error: any) {
        // Ne pas logger les erreurs 401 (session expirée)
        if (
          error?.response?.status !== 401 &&
          !error?.includes?.("Session expirée")
        ) {
          console.error("Erreur lors du chargement des positions:", error);
        }
        setPositions([]);
      } finally {
        if (!silent) setLoadingPositions(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setPnlTransactions([]);
      return;
    }
    void loadPnlHistory({ silent: false });
    const interval = setInterval(() => {
      void loadPnlHistory({ silent: true });
    }, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, loadPnlHistory]);

  useEffect(() => {
    return () => {
      if (closeIndicatorTimeout.current) {
        clearTimeout(closeIndicatorTimeout.current);
      }
    };
  }, []);

  // Callback stable pour éviter les reconnexions
  const handleWebSocketMessage = useCallback((data: Ticker) => {
    if (data.price) {
      setCurrentPrice(data.price);
    }
  }, []);

  // WebSocket pour les données en temps réel
  const { isConnected } = useWebSocket({
    symbol: selectedPair?.symbol || "BTC/USD",
    enabled: !!selectedPair,
    onMessage: handleWebSocketMessage,
  });

  // Charger les paires disponibles (génériques + projets)
  useEffect(() => {
    let isMounted = true;
    let hasLoaded = false;

    const loadPairs = async () => {
      // Éviter les appels multiples
      if (hasLoaded) return;
      hasLoaded = true;

      try {
        // Charger les paires génériques et les projets en parallèle
        // Utiliser catch pour éviter que les erreurs bloquent le chargement
        const [genericPairs, projects] = await Promise.all([
          marketService.getPopularPairs().catch((err) => {
            console.warn(
              "Erreur lors du chargement des paires génériques:",
              err,
            );
            return [] as MarketPair[];
          }),
          projectService.listProjects().catch(() => [] as Project[]),
        ]);

        if (!isMounted) return;

        // Filtrer uniquement les projets ouverts
        const openProjects = projects.filter((p) => p.status === "open");

        // Convertir les projets en MarketPair
        // Filtrer les null (projets dont le symbole n'existe pas sur Binance)
        const projectPairsResults = await Promise.allSettled(
          openProjects.map((project) => projectToMarketPair(project)),
        );
        const projectPairs = projectPairsResults
          .filter(
            (result): result is PromiseFulfilledResult<MarketPair> =>
              result.status === "fulfilled" && result.value !== null,
          )
          .map((result) => result.value);

        if (!isMounted) return;

        // Combiner les paires (projets en premier)
        const allPairs = [...projectPairs, ...genericPairs];
        setAvailablePairs(allPairs);

        // Sélectionner la première paire par défaut seulement si aucune n'est sélectionnée
        if (allPairs.length > 0 && !selectedPair) {
          setSelectedPair(allPairs[0]);
          setCurrentPrice(allPairs[0].price);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des paires:", error);
        // S'assurer qu'on a au moins les paires de fallback
        setAvailablePairs([]);
      } finally {
        hasLoaded = false; // Permettre un rechargement si nécessaire
      }
    };
    void loadPairs();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePairSelect = useCallback((pair: MarketPair) => {
    setSelectedPair(pair);
    setCurrentPrice(pair.price);
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    hasLoadedOrdersRef.current = false;
    hasLoadedHistoryRef.current = false;
    void fetchBalances(user.id);
    void loadTradingAccounts({ silent: false });
    void loadPositions({ silent: false });

    const positionsInterval = setInterval(() => {
      void loadPositions({ silent: true });
    }, 30000);

    const accountsInterval = setInterval(() => {
      void loadTradingAccounts({ silent: true });
    }, 30000);

    return () => {
      clearInterval(positionsInterval);
      clearInterval(accountsInterval);
    };
    // Ne dépendre que de user?.id pour éviter les boucles infinies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const primaryBalance = useMemo(() => {
    if (!balances || balances.breakdown.length === 0) {
      return null;
    }
    return balances.breakdown[0];
  }, [balances]);

  // Solde global disponible, affiché en USD et jamais négatif.
  const globalAvailableBalanceUsd = useMemo(() => {
    if (!balances) {
      return null;
    }

    const rawTotal = balances.totalAvailable ?? 0;
    const nonNegativeTotal = Math.max(0, rawTotal);

    // Si l'utilisateur n'a qu'une seule devise et que c'est de l'EUR,
    // on convertit en USD pour l'affichage.
    const hasSingleEur =
      balances.breakdown.length === 1 &&
      balances.breakdown[0].currency?.toUpperCase?.() === "EUR";

    const rate = hasSingleEur ? EUR_TO_USD_RATE : 1;

    return nonNegativeTotal * rate;
  }, [balances]);

  // Charger les ordres ouverts
  const loadOpenOrders = useCallback(
    async (options?: { background?: boolean }) => {
      const isBackground = options?.background ?? false;
      if (!user) {
        setOpenOrders([]);
        if (!isBackground) {
          setLoadingOrders(false);
        }
        return;
      }
      const shouldShowLoading = !isBackground && !hasLoadedOrdersRef.current;
      try {
        if (shouldShowLoading) {
          setLoadingOrders(true);
        }
        const orders = await orderService.listOpenOrders();
        setOpenOrders(orders);
        const sig = orders
          .map((o) => `${o.id}:${o.status}:${o.filledQuantity}`)
          .join("|");
        lastOpenOrdersSigRef.current = sig;
        hasLoadedOrdersRef.current = true;
      } catch (error: any) {
        // Ne pas logger les erreurs 401 (session expirée)
        if (
          error?.response?.status !== 401 &&
          !error?.includes?.("Session expirée")
        ) {
          console.error("Erreur lors du chargement des ordres:", error);
        }
        if (!isBackground && !hasLoadedOrdersRef.current) {
          setOpenOrders([]);
        }
      } finally {
        if (!isBackground && shouldShowLoading) {
          setLoadingOrders(false);
        }
      }
    },
    [user],
  );

  const loadOrderHistory = useCallback(
    async (options?: { background?: boolean }) => {
      const isBackground = options?.background ?? false;
      if (!user) {
        setOrderHistory([]);
        if (!isBackground) {
          setLoadingHistory(false);
        }
        return;
      }
      const shouldShowLoading = !isBackground && !hasLoadedHistoryRef.current;
      try {
        if (shouldShowLoading) {
          setLoadingHistory(true);
        }
        const orders = await orderService.listOrders();
        const slice = orders.slice(0, 5);
        setOrderHistory(slice);
        const sig = slice
          .map((o) => `${o.id}:${o.status}:${o.filledQuantity}`)
          .join("|");
        lastOrderHistorySigRef.current = sig;

        // Notifier les ordres annulés
        orders
          .filter(
            (order) =>
              order.status === "cancelled" && order.cancelReason?.length,
          )
          .forEach((order) => {
            if (!cancelledOrdersNotifiedRef.current.has(order.id)) {
              cancelledOrdersNotifiedRef.current.add(order.id);
              toast({
                title: "Ordre annulé",
                description: order.cancelReason,
                variant: "destructive",
              });
            }
          });

        // Notifier les ordres exécutés (filled)
        orders
          .filter((order) => order.status === "filled")
          .forEach((order) => {
            if (!executedOrdersNotifiedRef.current.has(order.id)) {
              executedOrdersNotifiedRef.current.add(order.id);
              const baseAsset = order.symbol.split("/")[0];
              const priceInfo =
                order.type === "LIMIT" && order.executedPrice
                  ? `à $${order.executedPrice}`
                  : "au marché";
              toast({
                title: "Ordre exécuté",
                description: `${order.side} ${order.quantity} ${baseAsset} ${priceInfo}`,
                variant: "success",
              });
            }
          });

        hasLoadedHistoryRef.current = true;
      } catch (error: any) {
        // Ne pas logger les erreurs 401 (session expirée)
        if (
          error?.response?.status !== 401 &&
          !error?.includes?.("Session expirée")
        ) {
          console.error("Erreur lors du chargement de l'historique:", error);
        }
        if (!isBackground && !hasLoadedHistoryRef.current) {
          setOrderHistory([]);
        }
      } finally {
        if (!isBackground && shouldShowLoading) {
          setLoadingHistory(false);
        }
      }
    },
    [user, toast],
  );

  useEffect(() => {
    if (!user) {
      setOpenOrders([]);
      setLoadingOrders(false);
      previousOpenOrdersRef.current = [];
      lastOpenOrdersSigRef.current = "";
      return;
    }
    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const load = async () => {
      if (!isMounted || !user) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      const shouldShowLoading = !hasLoadedOrdersRef.current;
      try {
        if (shouldShowLoading) {
          setLoadingOrders(true);
        }
        const orders = await orderService.listOpenOrders();
        if (isMounted && user) {
          // Détecter les ordres qui ont été exécutés (disparus de la liste des ordres ouverts)
          const currentOrderIds = new Set(orders.map((o) => o.id));

          // Trouver les ordres qui étaient ouverts mais ne le sont plus
          previousOpenOrdersRef.current.forEach((previousOrder) => {
            if (!currentOrderIds.has(previousOrder.id)) {
              // L'ordre n'est plus dans la liste des ordres ouverts
              // Vérifier s'il a été exécuté (et non pas annulé manuellement)
              // On vérifie dans l'historique pour confirmer qu'il est "filled"
              orderService
                .listOrders()
                .then((allOrders) => {
                  if (!isMounted || !user) return;
                  const executedOrder = allOrders.find(
                    (o) => o.id === previousOrder.id && o.status === "filled",
                  );
                  if (
                    executedOrder &&
                    !executedOrdersNotifiedRef.current.has(executedOrder.id)
                  ) {
                    executedOrdersNotifiedRef.current.add(executedOrder.id);
                    const baseAsset = executedOrder.symbol.split("/")[0];
                    const priceInfo =
                      executedOrder.type === "LIMIT" &&
                      executedOrder.executedPrice
                        ? `à $${executedOrder.executedPrice}`
                        : "au marché";
                    toast({
                      title: "Ordre exécuté",
                      description: `${executedOrder.side} ${executedOrder.quantity} ${baseAsset} ${priceInfo}`,
                      variant: "success",
                    });
                  }
                })
                .catch((error: any) => {
                  // Si erreur 401, arrêter l'intervalle
                  if (
                    error?.response?.status === 401 ||
                    error?.includes?.("Session expirée")
                  ) {
                    if (intervalId) {
                      clearInterval(intervalId);
                      intervalId = null;
                    }
                    return;
                  }
                  console.error(
                    "Erreur lors de la vérification de l'ordre exécuté:",
                    error,
                  );
                });
            }
          });

          previousOpenOrdersRef.current = orders;
          const sig = orders
            .map((o) => `${o.id}:${o.status}:${o.filledQuantity}`)
            .join("|");
          if (sig !== lastOpenOrdersSigRef.current) {
            lastOpenOrdersSigRef.current = sig;
            setOpenOrders(orders);
          }
          hasLoadedOrdersRef.current = true;
        }
      } catch (error: any) {
        // Si erreur 401, arrêter l'intervalle immédiatement
        if (
          error?.response?.status === 401 ||
          error?.includes?.("Session expirée") ||
          (typeof error === "string" && error.includes("Session expirée"))
        ) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          if (isMounted) {
            setOpenOrders([]);
            setLoadingOrders(false);
          }
          isMounted = false;
          return;
        }
        console.error("Erreur lors du chargement des ordres:", error);
      } finally {
        if (isMounted && shouldShowLoading) {
          setLoadingOrders(false);
        }
      }
    };

    void load();
    intervalId = setInterval(() => {
      if (!user || !isMounted) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      void load();
    }, 5000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, toast]);

  useEffect(() => {
    if (!user) {
      setOrderHistory([]);
      setLoadingHistory(false);
      lastOrderHistorySigRef.current = "";
      return;
    }
    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const load = async () => {
      if (!isMounted || !user) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      const shouldShowLoading = !hasLoadedHistoryRef.current;
      try {
        if (shouldShowLoading) {
          setLoadingHistory(true);
        }
        const orders = await orderService.listOrders();
        if (isMounted && user) {
          const slice = orders.slice(0, 5);
          const sig = slice
            .map((o) => `${o.id}:${o.status}:${o.filledQuantity}`)
            .join("|");
          if (sig !== lastOrderHistorySigRef.current) {
            lastOrderHistorySigRef.current = sig;
            setOrderHistory(slice);
          }
          hasLoadedHistoryRef.current = true;
        }
      } catch (error: any) {
        // Si erreur 401, arrêter l'intervalle immédiatement
        if (
          error?.response?.status === 401 ||
          error?.includes?.("Session expirée") ||
          (typeof error === "string" && error.includes("Session expirée"))
        ) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          if (isMounted) {
            setOrderHistory([]);
            setLoadingHistory(false);
          }
          isMounted = false;
          return;
        }
        console.error("Erreur lors du chargement de l'historique:", error);
      } finally {
        if (isMounted && shouldShowLoading) {
          setLoadingHistory(false);
        }
      }
    };

    void load();
    intervalId = setInterval(() => {
      if (!user || !isMounted) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      void load();
    }, 10000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // Utiliser user.id au lieu de user pour éviter les re-renders inutiles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleOrderSubmit = async (order: OrderPayload) => {
    if (!user) {
      toast({
        title: t("trading.loginRequiredTitle", "Acheter maintenant"),
        description: t(
          "trading.loginRequiredDescription",
          "Vous devez être connecté pour pouvoir passer des ordres.",
        ),
        variant: "info",
      });
      return;
    }
    try {
      await orderService.createOrder({
        symbol: order.symbol,
        side: order.side,
        type: order.type,
        quantity: order.quantity,
        price: order.price,
        stopPrice: order.stopPrice,
        stopLoss: order.stopLoss,
        takeProfit: order.takeProfit,
      });

      const asset = order.symbol.split("/")[0];
      const priceText =
        order.type === "MARKET"
          ? i18n.language.startsWith("en")
            ? "market"
            : i18n.language.startsWith("de")
              ? "Markt"
              : i18n.language.startsWith("pt")
                ? "mercado"
                : "marché"
          : `$${order.price}`;
      const sideText =
        order.side === "BUY"
          ? i18n.language.startsWith("en")
            ? "Buy"
            : i18n.language.startsWith("de")
              ? "Kaufen"
              : i18n.language.startsWith("pt")
                ? "Compra"
                : "Achat"
          : i18n.language.startsWith("en")
            ? "Sell"
            : i18n.language.startsWith("de")
              ? "Verkaufen"
              : i18n.language.startsWith("pt")
                ? "Venda"
                : "Vente";

      toast({
        title: t("trading.orderCreatedTitle", "Ordre créé"),
        description: t("trading.orderCreatedDescription", {
          side: sideText,
          quantity: order.quantity,
          asset,
          price: priceText,
        }),
        variant: "success",
      });

      await Promise.all([
        loadOpenOrders({ background: true }),
        loadOrderHistory({ background: true }),
      ]);
      await Promise.all([
        loadTradingAccounts(),
        loadPositions({ silent: true }),
      ]);
    } catch (error) {
      console.error("Erreur lors de la création de l'ordre:", error);
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : t(
                "trading.orderCreateErrorDescription",
                "Impossible de créer l'ordre.",
              );
      toast({
        title: t("trading.errorTitle", "Erreur"),
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleClosePosition = async (symbol: string) => {
    try {
      await tradingService.closePosition(symbol);
      toast({
        title: t("trading.positionClosedTitle", "Position clôturée"),
        description: t(
          "trading.positionClosedDescription",
          `Votre position sur ${symbol} a été fermée.`,
        ),
        variant: "success",
      });
      setRecentClosedSymbol(symbol);
      if (closeIndicatorTimeout.current) {
        clearTimeout(closeIndicatorTimeout.current);
      }
      closeIndicatorTimeout.current = setTimeout(() => {
        setRecentClosedSymbol(null);
      }, 4000);
      await Promise.all([
        loadOpenOrders({ background: true }),
        loadOrderHistory({ background: true }),
        loadTradingAccounts(),
        loadPositions({ silent: true }),
      ]);
    } catch (error) {
      console.error("Erreur lors de la clôture de la position:", error);
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : t(
                "trading.positionCloseErrorDescription",
                "Impossible de clôturer la position.",
              );
      toast({
        title: t("trading.errorTitle", "Erreur"),
        description: message,
        variant: "destructive",
      });
    }
  };

  if (!selectedPair) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-slate-600 dark:text-white/70">
          {t(
            "trading.loadingMarketData",
            "Chargement des données de marché...",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
      <div className="w-full">
        {/* En-tête avec sélection de paire et indicateurs */}
        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 text-foreground shadow-sm backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/50 dark:to-slate-800/30 dark:text-white dark:shadow-lg dark:shadow-black/20">
          <div className="flex flex-wrap items-center gap-4">
            <Select
              value={selectedPair.symbol}
              onValueChange={(value: string) => {
                const pair = availablePairs.find((p) => p.symbol === value);
                if (pair) {
                  setSelectedPair(pair);
                  setCurrentPrice(pair.price);
                }
              }}>
              <SelectTrigger className="w-40 border-border bg-background shadow-sm dark:border-white/10 dark:bg-slate-800/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availablePairs.map((pair) => (
                  <SelectItem key={pair.symbol} value={pair.symbol}>
                    {pair.symbol}{" "}
                    {pair.projectName ? `(${pair.projectName})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <p className="text-3xl font-bold text-foreground dark:text-white">
                {currentPrice.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}
              </p>
              <p
                className={`text-sm font-medium tabular-nums ${
                  selectedPair.changePercent24h >= 0
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-rose-700 dark:text-rose-300"
                }`}>
                {selectedPair.changePercent24h >= 0 ? "+" : ""}
                {selectedPair.changePercent24h.toFixed(2)}% (24h)
              </p>
            </div>
            <div className="flex flex-col gap-1 rounded-2xl border border-border bg-muted/50 px-4 py-3 text-foreground/90 shadow-md dark:border-white/5 dark:bg-white/5 dark:text-white/80 dark:shadow-black/10">
              <div className="text-xs uppercase text-muted-foreground dark:text-white/60">
                {t("trading.headerAvailableBalance")}
              </div>
              <div className="text-lg font-semibold text-foreground dark:text-white">
                {!isAuthenticated
                  ? t("trading.loginRequiredTitle")
                  : balancesLoading
                    ? t("trading.loading")
                    : globalAvailableBalanceUsd !== null
                      ? `${globalAvailableBalanceUsd.toFixed(2)} $`
                      : primaryBalance
                        ? `${primaryBalance.available.toFixed(2)} ${
                            primaryBalance.currency
                          }`
                        : balances
                          ? `${Math.max(0, balances.totalAvailable).toFixed(2)} $`
                          : "—"}
              </div>
              {isAuthenticated && balances && (
                <div className="text-xs text-muted-foreground dark:text-white/60">
                  {t("trading.headerInvested", {
                    amount: balances.invested.toFixed(2),
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  isConnected ? "bg-green-400" : "bg-red-400"
                }`}
              />
              <span className="text-xs text-muted-foreground dark:text-white/70">
                {isConnected
                  ? t("trading.headerConnected")
                  : t("trading.headerDisconnected")}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground dark:text-white/70">
            <span>RSI</span>
            <span>MACD</span>
            <span>BB</span>
          </div>
        </div>

        {/* Layout principal */}
        <div className="grid grid-cols-1 gap-4 min-[1200px]:grid-cols-[minmax(0,2.3fr)_minmax(0,1.2fr)]">
          {/* Colonne centrale : Graphique (terminal principal) */}
          <div className="space-y-4">
            {/* Graphique en chandeliers */}
            <div className="relative h-[500px]">
              {recentClosedSymbol && (
                <div className="pointer-events-none absolute right-4 top-4 z-10 rounded-xl border border-emerald-400 bg-black/70 px-4 py-2 text-sm text-white shadow-lg">
                  Position{" "}
                  <span className="font-semibold">{recentClosedSymbol}</span>{" "}
                  clôturée
                </div>
              )}
              <ProfessionalCandlestickChart
                symbol={selectedPair.symbol}
                timeframe={timeframe}
                height={500}
                positions={positions}
                onTimeframeChange={setTimeframe}
                userId={user?.id ?? null}
              />
            </div>

            {/* Indicateurs techniques (les timeframes sont dans le graphique) */}
            <div className="flex items-center justify-end gap-3 rounded-2xl border border-border bg-muted/40 dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/50 dark:to-slate-800/30 backdrop-blur-sm p-4 shadow-lg shadow-black/20">
              <div className="flex gap-3 text-xs text-muted-foreground dark:text-white/70">
                <span>H1</span>
                <span>MACD</span>
                <span>S1</span>
              </div>
            </div>
          </div>

          {/* Colonne droite : Watchlist, DOM, Ordres dans un conteneur scrollable */}
          <div className="rounded-2xl border border-border bg-card p-4 shadow-lg shadow-black/10 min-[1200px]:h-[600px] dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40 dark:shadow-black/30">
            <div className="flex h-full flex-col gap-3 overflow-y-auto pr-1 scrollbar-thin">
              {/* Watchlist + DOM en ligne, puis colonne < 400px */}
              <div className="flex flex-row gap-3 max-[399px]:flex-col max-[399px]:gap-2">
                <div className="w-1/2 max-[399px]:w-full">
                  <Watchlist
                    onSelectPair={handlePairSelect}
                    selectedSymbol={selectedPair.symbol}
                  />
                </div>

                <div className="w-1/2 rounded-2xl border border-border bg-card p-3 max-[399px]:w-full dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40">
                  <DepthOfMarket symbol={selectedPair.symbol} maxEntries={8} />
                </div>
              </div>

              {/* Panneau d'ordre */}
              <div className="rounded-2xl border border-border bg-card p-3 dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40">
                {isAuthenticated ? (
                  <OrderPanel
                    symbol={selectedPair.symbol}
                    currentPrice={currentPrice}
                    availableBase={
                      balances?.breakdown.find(
                        (balance) =>
                          balance.currency === selectedPair.baseAsset,
                      )?.available ?? 0
                    }
                    availableQuote={
                      balances?.breakdown.find(
                        (balance) =>
                          balance.currency === selectedPair.quoteAsset,
                      )?.available ?? 0
                    }
                    onOrderSubmit={handleOrderSubmit}
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 text-center text-muted-foreground shadow-inner backdrop-blur-sm dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-900/30 dark:to-slate-800/20 dark:text-white/70">
                    <p className="mb-2 text-lg font-semibold">
                      {t("trading.loginToTradeTitle")}
                    </p>
                    <p className="text-sm">
                      {t("trading.loginToTradeDescription")}
                    </p>
                  </div>
                )}
              </div>

              {/* Ordres ouverts */}
              <div className="rounded-2xl border border-border bg-card p-3 dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40">
                <h3 className="mb-3 text-lg font-semibold text-foreground dark:text-white">
                  {t("trading.openOrdersTitle", { count: openOrders.length })}
                </h3>
                {!isAuthenticated ? (
                  <p className="text-sm text-muted-foreground dark:text-white/70">
                    {t("trading.openOrdersLogin")}
                  </p>
                ) : loadingOrders ? (
                  <p className="text-sm text-muted-foreground dark:text-white/70">
                    {t("trading.loading")}
                  </p>
                ) : openOrders.length === 0 ? (
                  <p className="text-sm text-muted-foreground dark:text-white/70">
                    {t("trading.noOpenOrders")}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto scrollbar-thin">
                    {openOrders.map((order) => (
                      <div
                        key={order.id}
                        className={`rounded-lg border backdrop-blur-sm p-2 text-xs shadow-sm transition-all hover:shadow-md ${
                          order.isSimulated
                            ? "border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/15"
                            : "border-border bg-muted/50 hover:bg-muted dark:border-white/5 dark:bg-white/5 dark:hover:bg-white/10"
                        }`}>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  order.type === "LIMIT"
                                    ? "bg-amber-500/20 text-amber-300"
                                    : order.type === "STOP_LIMIT"
                                      ? "bg-purple-500/20 text-purple-300"
                                      : "bg-sky-500/20 text-sky-300"
                                }`}>
                                {order.type}
                              </span>
                              {order.isSimulated && (
                                <span className="rounded-full bg-sky-500/20 px-2 py-0.5 text-[10px] font-medium text-sky-200/90">
                                  Simulé
                                </span>
                              )}
                            </div>
                            <span
                              className={`font-semibold tabular-nums ${
                                order.side === "BUY"
                                  ? "text-emerald-800 dark:text-emerald-300"
                                  : "text-rose-800 dark:text-rose-300"
                              }`}>
                              {order.side} {Number(order.quantity).toFixed(8)}{" "}
                              {order.symbol.split("/")[0]}
                            </span>
                            {order.type === "STOP_LIMIT" &&
                              order.stopPrice !== null && (
                                <span className="text-[11px] text-muted-foreground dark:text-white/50">
                                  Stop {order.stopPrice.toFixed(2)}
                                </span>
                              )}
                          </div>
                          <span className="text-xs text-muted-foreground dark:text-white/70">
                            {order.type !== "MARKET" && order.price
                              ? t("trading.limitPriceLabel", {
                                  price: order.price,
                                })
                              : order.type === "MARKET"
                                ? t("trading.marketLabel")
                                : "-"}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground dark:text-white/50">
                          <span>
                            {order.status === "pending"
                              ? t("trading.statusPending")
                              : order.status === "partially_filled"
                                ? t("trading.statusPartial", {
                                    filled: Number(
                                      order.filledQuantity,
                                    ).toFixed(8),
                                    quantity: Number(order.quantity).toFixed(8),
                                  })
                                : order.status}
                          </span>
                          {order.type === "LIMIT" &&
                            order.status === "pending" && (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await orderService.cancelOrder(order.id);
                                    toast({
                                      title: t(
                                        "trading.cancelOrderSuccessTitle",
                                      ),
                                      description: t(
                                        "trading.cancelOrderSuccessDescription",
                                      ).replace("{{id}}", `${order.id}`),
                                      variant: "success",
                                    });
                                    await Promise.all([
                                      loadOpenOrders({ background: true }),
                                      loadOrderHistory({ background: true }),
                                    ]);
                                  } catch (error) {
                                    const message =
                                      typeof error === "string"
                                        ? error
                                        : error instanceof Error
                                          ? error.message
                                          : t(
                                              "trading.cancelOrderErrorDescription",
                                            );
                                    toast({
                                      title: t("trading.errorTitle", "Erreur"),
                                      description: message,
                                      variant: "destructive",
                                    });
                                  }
                                }}
                                className="rounded border border-border px-2 py-0.5 text-[10px] text-muted-foreground hover:border-rose-400 hover:text-rose-600 dark:border-white/20 dark:text-white/70 dark:hover:text-rose-300">
                                Annuler
                              </button>
                            )}
                        </div>
                        {order.cancelReason && (
                          <p className="mt-1 text-[10px] text-rose-300">
                            {order.cancelReason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Historique récent */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-border bg-card p-3 dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40">
                  <h3 className="mb-3 text-lg font-semibold text-foreground dark:text-white">
                    {t("trading.recentHistoryTitle")}
                  </h3>
                  {!isAuthenticated ? (
                    <p className="text-sm text-muted-foreground dark:text-white/70">
                      {t("trading.recentHistoryLogin")}
                    </p>
                  ) : loadingHistory ? (
                    <p className="text-sm text-muted-foreground dark:text-white/70">
                      {t("trading.loading")}
                    </p>
                  ) : orderHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground dark:text-white/70">
                      {t("trading.noRecentOrders")}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto scrollbar-thin">
                      {orderHistory.map((order) => (
                        <div
                          key={`history-${order.id}`}
                          className={`rounded-lg border backdrop-blur-sm p-2 text-xs shadow-sm transition-all hover:shadow-md ${
                            order.isSimulated
                              ? "border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/15"
                              : "border-border bg-muted/50 hover:bg-muted dark:border-white/5 dark:bg-white/5 dark:hover:bg-white/10"
                          }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span
                                className={`font-semibold tabular-nums ${
                                  order.side === "BUY"
                                    ? "text-emerald-800 dark:text-emerald-300"
                                    : "text-rose-800 dark:text-rose-300"
                                }`}>
                                {order.side}
                              </span>
                              {order.isSimulated && (
                                <span className="rounded-full bg-sky-500/20 px-2 py-0.5 text-[10px] font-medium text-sky-200/90">
                                  {t("trading.simulatedBadge")}
                                </span>
                              )}
                            </div>
                            <span className="text-muted-foreground dark:text-white/70">
                              {order.symbol}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center justify-between text-muted-foreground dark:text-white/60">
                            <span>{Number(order.quantity).toFixed(8)}</span>
                            <span className="text-muted-foreground dark:text-white/70">
                              {order.executedPrice !== null &&
                              order.executedPrice !== undefined
                                ? `$${Number(order.executedPrice).toFixed(2)}`
                                : order.type === "MARKET"
                                  ? t("trading.marketLabel")
                                  : order.price !== null &&
                                      order.price !== undefined
                                    ? `$${order.price}`
                                    : "-"}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center justify-between text-muted-foreground dark:text-white/50">
                            <span>
                              {new Date(order.createdAt).toLocaleTimeString(
                                "fr-FR",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                            <div className="flex items-center gap-2">
                              {order.isSimulated && (
                                <span className="text-[10px] text-sky-200/80">
                                  {t("trading.robotBadge")}
                                </span>
                              )}
                              <span>
                                {order.status === "filled"
                                  ? t("trading.recentStatusFilled")
                                  : order.status === "rejected"
                                    ? t("trading.recentStatusRejected")
                                    : order.status === "pending"
                                      ? t("trading.recentStatusPending")
                                      : order.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Historique P&L (gains/pertes sur trades) */}
                <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/60 to-slate-900/60 p-3">
                  <h3 className="mb-3 text-lg font-semibold text-foreground dark:text-white">
                    {t(
                      "trading.pnlHistoryTitle",
                      "Historique des gains/pertes",
                    )}
                  </h3>
                  {!isAuthenticated ? (
                    <p className="text-sm text-muted-foreground dark:text-white/70">
                      {t(
                        "trading.pnlHistoryLogin",
                        "Connectez-vous pour voir l'historique de vos gains/pertes.",
                      )}
                    </p>
                  ) : !pnlTransactions.length ? (
                    <p className="text-sm text-muted-foreground dark:text-white/70">
                      {t(
                        "trading.noPnlHistory",
                        "Aucun gain/perte enregistré pour le moment.",
                      )}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto scrollbar-thin">
                      {paginatedPnlTransactions.map((tx) => {
                        let isProfit = false;
                        let symbol: string | undefined;
                        let quantitySold: number | undefined;
                        try {
                          if (tx.metadata) {
                            const meta = JSON.parse(tx.metadata) as {
                              isProfit?: boolean;
                              symbol?: string;
                              quantitySold?: number;
                            };
                            if (typeof meta.isProfit === "boolean") {
                              isProfit = meta.isProfit;
                            }
                            if (
                              typeof meta.symbol === "string" &&
                              meta.symbol
                            ) {
                              symbol = meta.symbol;
                            }
                            if (
                              typeof meta.quantitySold === "number" &&
                              meta.quantitySold > 0
                            ) {
                              quantitySold = meta.quantitySold;
                            }
                          }
                        } catch {
                          // ignore JSON errors, fallback later if needed
                        }
                        const value = isProfit ? tx.amount : -tx.amount;
                        const formatted = `${value >= 0 ? "+" : "-"}${Math.abs(
                          value,
                        ).toFixed(2)} ${pnlCurrency}`;
                        const baseAsset = symbol?.split("/")[0];
                        const quantityLabel =
                          quantitySold !== undefined && baseAsset
                            ? `${quantitySold
                                .toFixed(8)
                                .replace(/\.?0+$/, "")} ${baseAsset}`
                            : null;
                        const rightLabel = [
                          symbol ??
                            t("trading.pnlHistoryTradeSettlement", "Trade"),
                          quantityLabel,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <div
                            key={tx.id}
                            className="flex items-center justify-between rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs dark:border-white/10 dark:bg-black/30">
                            <div className="flex flex-col gap-0.5">
                              <span
                                className={
                                  value >= 0
                                    ? "font-semibold tabular-nums text-emerald-800 dark:text-emerald-300"
                                    : "font-semibold tabular-nums text-rose-800 dark:text-rose-300"
                                }>
                                {formatted}
                              </span>
                              <span className="text-xs text-foreground/70 dark:text-white/70">
                                {new Date(tx.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <span className="max-w-[50%] text-right text-xs text-foreground/75 dark:text-white/70">
                              {rightLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {pnlTransactions.length > pnlHistoryPerPage && (
                    <div className="mt-2">
                      <Pagination
                        currentPage={pnlHistoryPage}
                        totalPages={totalPnlPages}
                        totalItems={pnlTransactions.length}
                        itemsPerPage={pnlHistoryPerPage}
                        onPageChange={setPnlHistoryPage}
                        isLoading={loadingPnlHistory}
                        ariaLabelPrevious={t(
                          "trading.pagination.previous",
                          "Précédent",
                        )}
                        ariaLabelNext={t("trading.pagination.next", "Suivant")}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {isAuthenticated && (
          <div className="mt-6 space-y-4">
            <section className="rounded-3xl border border-border bg-card p-4 text-foreground dark:border-white/5 dark:bg-white/5/10 dark:text-white">
              <h3 className="text-lg font-semibold">
                {t("trading.tradingAccountsTitle")}
              </h3>
              {loadingAccounts ? (
                <p className="text-sm text-muted-foreground dark:text-white/70 mt-2">
                  {t("trading.tradingAccountsLoading")}
                </p>
              ) : tradingAccounts.filter(
                  (account) => account.currency !== "EUR",
                ).length === 0 ? (
                <p className="text-sm text-muted-foreground dark:text-white/70 mt-2">
                  {t("trading.noTradingAccounts")}
                </p>
              ) : (
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  {tradingAccounts
                    .filter((account) => account.currency !== "EUR")
                    .map((account) => {
                      const available = account.balance - account.lockedBalance;
                      return (
                        <div
                          key={`${account.currency}-${account.id}`}
                          className="rounded-2xl border border-border bg-card p-4 dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40">
                          <p className="text-xs uppercase text-muted-foreground dark:text-white/60">
                            {account.currency}
                          </p>
                          <p className="text-2xl font-semibold">
                            {account.balance.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground dark:text-white/60">
                            {t("trading.tradingAvailable", {
                              amount: available.toFixed(2),
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground dark:text-white/60">
                            {t("trading.tradingLocked", {
                              amount: account.lockedBalance.toFixed(2),
                            })}
                          </p>
                        </div>
                      );
                    })}
                </div>
              )}
            </section>

            <section className="rounded-3xl border border-border bg-card p-4 text-foreground dark:border-white/5 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/30 dark:text-white">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    {t("trading.positionsTitle", { count: positions.length })}
                  </h3>
                  <p className="text-sm text-muted-foreground dark:text-white/60">
                    {t("trading.positionsSubtitle")}
                  </p>
                </div>
              </div>
              {loadingPositions ? (
                <p className="text-sm text-muted-foreground dark:text-white/70">
                  {t("trading.closePositionLoading")}
                </p>
              ) : positions.length === 0 ? (
                <p className="text-sm text-muted-foreground dark:text-white/70">
                  {t("trading.noPositions")}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-foreground/90 dark:text-white/80">
                    <thead>
                      <tr className="text-xs uppercase text-muted-foreground dark:text-white/50">
                        <th className="pb-2">Symbol</th>
                        <th className="pb-2">Quantité</th>
                        <th className="pb-2">Prix moyen</th>
                        <th className="pb-2">Prix actuel</th>
                        <th className="pb-2">Valeur</th>
                        <th className="pb-2">P&L</th>
                        <th className="pb-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPositions.map((position) => (
                        <tr
                          key={position.marketSymbol}
                          className="border-t border-border dark:border-white/5">
                          <td className="py-3 font-semibold">
                            {position.marketSymbol}
                          </td>
                          <td>{position.quantity.toFixed(6)}</td>
                          <td>${position.averageEntryPrice.toFixed(2)}</td>
                          <td>${position.currentPrice.toFixed(2)}</td>
                          <td>
                            {position.currentValue.toFixed(2)}{" "}
                            {position.quoteCurrency}
                          </td>
                          <td
                            className={
                              position.pnlValue >= 0
                                ? "tabular-nums text-emerald-800 dark:text-emerald-300"
                                : "tabular-nums text-rose-800 dark:text-rose-300"
                            }>
                            {position.pnlValue >= 0 ? "+" : ""}
                            {position.pnlValue.toFixed(2)} (
                            {position.pnlPercent >= 0 ? "+" : ""}
                            {position.pnlPercent.toFixed(2)}%)
                          </td>
                          <td className="text-right">
                            <button
                              onClick={() =>
                                handleClosePosition(position.marketSymbol)
                              }
                              className="rounded-lg border border-border px-3 py-1 text-xs font-medium text-foreground/90 transition hover:border-border hover:bg-muted dark:border-white/10 dark:text-white/80 dark:hover:border-white/30 dark:hover:bg-white/10">
                              {t("trading.closePositionButton")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {positions.length > positionsPerPage && (
                    <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
                      <Pagination
                        currentPage={positionsPage}
                        totalPages={totalPositionsPages}
                        totalItems={positions.length}
                        itemsPerPage={positionsPerPage}
                        onPageChange={setPositionsPage}
                        ariaLabelPrevious={t("trading.pagination.previous")}
                        ariaLabelNext={t("trading.pagination.next")}
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingPage;
